/**
 * 🌤️ WORKOUT CARD WEATHER COMPONENT
 * Displays weather forecast for a specific workout date
 * 
 * Mission 14: Weather & Climate Intelligence - Phase 3 Frontend
 * This is an alias for TodayWeatherBand with date prop for clarity
 */

import React from 'react';
import { TodayWeatherBand } from './TodayWeatherBand';
import { storage } from '../services/storage';

/**
 * WorkoutCardWeather Component
 * 
 * Shows weather forecast for a specific workout date
 * Reads from backend 7-day forecast cache (no additional API calls)
 * 
 * @example
 * ```tsx
 * // On workout card:
 * <WorkoutCardWeather 
 *   workoutDate={workout.scheduledDate} 
 *   size="sm" 
 * />
 * 
 * // On workout detail page:
 * <WorkoutCardWeather 
 *   workoutDate={workout.scheduledDate} 
 *   size="md"
 *   className="mb-4" 
 * />
 * ```
 */
export const WorkoutCardWeather: React.FC<{
  workoutDate: string | Date;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({
  workoutDate,
  size = 'sm',
  className = ''
}) => {
  // Simple alias to TodayWeatherBand with date prop
  // TodayWeatherBand handles:
  // - Fetching forecast from cache
  // - Finding forecast for specific date
  // - Graceful degradation if forecast unavailable
  return (
    <TodayWeatherBand 
      date={workoutDate}
      size={size}
      className={className}
    />
  );
};

/**
 * WorkoutWeatherDetail Component
 * 
 * Detailed weather display for workout detail pages
 * Shows NOW, HIGH, LOW and conditions
 * 
 * 🎯 BUG FIX (Nov 2025): Enhanced weather display per user feedback
 * - Shows: NOW (if today), High, Low
 * - Clean, compact design for mobile
 * 
 * 🆕 Sprint 3 Task 3.3: Weather guidance text support
 * - Displays weather guidance from Phase 3 LLM if available
 * 
 * @example
 * ```tsx
 * <WorkoutWeatherDetail 
 *   workoutDate={workout.scheduledDate} 
 *   guidance={workout.workout_data?.weather_guidance}
 * />
 * ```
 */
export const WorkoutWeatherDetail: React.FC<{
  workoutDate: string | Date;
  guidance?: string;
  className?: string;
}> = ({
  workoutDate,
  guidance,
  className = ''
}) => {
  const [weatherData, setWeatherData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchWeather = async () => {
      const jwtToken = await storage.getItem('jwt_token');
      if (!jwtToken) {
        setLoading(false);
        return;
      }

      try {
        // Use relative URL - works with proxy or same-origin (original working approach)
        const response = await fetch('/api/weather/cached-forecast', {
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Verify response is JSON before parsing
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            // Suppress console warnings for expected 404s
            setLoading(false);
            return; // Graceful degradation
          }
          const data = await response.json();
          setWeatherData(data);
        } else if (response.status === 404) {
          // Suppress 404 errors (expected when location not confirmed)
          setLoading(false);
          return;
        }
      } catch (err) {
        // Suppress 404 errors (expected when location not confirmed)
        if (!(err instanceof Error && err.message.includes('404'))) {
          console.error('Weather fetch error:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []);

  if (loading || !weatherData) {
    return null;
  }

  // Determine if this is today's workout
  const targetDate = typeof workoutDate === 'string' ? new Date(workoutDate) : workoutDate;
  const today = new Date();
  const isToday = targetDate.toDateString() === today.toDateString();
  const targetDateString = targetDate.toISOString().split('T')[0];

  // Find forecast for this date
  const forecast = weatherData.forecast_7day?.find(
    (day: any) => day.date === targetDateString
  );

  if (!forecast) {
    return null;
  }

  // Determine temperature unit based on location (Celsius for most countries, Fahrenheit for US)
  // Check if location indicates US (country_code === 'US' or location contains US state codes)
  const usesFahrenheit = weatherData.location?.country_code === 'US' || 
                         weatherData.location?.country?.toLowerCase() === 'united states';
  
  // Helper to display temperature with correct unit
  const displayTemp = (tempC: number) => {
    if (usesFahrenheit) {
      const tempF = Math.round((tempC * 9/5) + 32);
      return `${tempF}°F`;
    }
    return `${Math.round(tempC)}°C`;
  };

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-3 border border-blue-100 ${className}`}>
      <div className="text-xs font-medium text-gray-600 mb-2">Weather Forecast</div>
      
      {/* 🔧 FIX JAN 22: Compact weather - condition inline with temps (not separate line) */}
      <div className="flex items-center gap-4">
        {/* Condition icon */}
        <div className="text-3xl">
          {forecast.condition.toLowerCase().includes('rain') ? '🌧️' :
           forecast.condition.toLowerCase().includes('cloud') ? '☁️' :
           forecast.condition.toLowerCase().includes('snow') ? '❄️' :
           forecast.condition.toLowerCase().includes('storm') ? '⛈️' :
           '☀️'}
        </div>
        
        {/* Temperature and condition grid - responsive columns based on whether today */}
        <div className={`flex-1 grid ${isToday ? 'grid-cols-4' : 'grid-cols-3'} gap-3 text-center`}>
          {/* NOW (if today) */}
          {isToday && weatherData.today?.temp_c && (
            <div>
              <div className="text-xs text-gray-500 font-medium">NOW</div>
              <div className="text-sm font-semibold text-gray-900">{displayTemp(weatherData.today.temp_c)}</div>
            </div>
          )}
          
          {/* HIGH */}
          <div>
            <div className="text-xs text-gray-500 font-medium">HIGH</div>
            <div className="text-sm font-semibold text-gray-900">{displayTemp(forecast.temp_high_c)}</div>
          </div>
          
          {/* LOW */}
          <div>
            <div className="text-xs text-gray-500 font-medium">LOW</div>
            <div className="text-sm font-semibold text-gray-900">{displayTemp(forecast.temp_low_c)}</div>
          </div>
          
          {/* CONDITION - now inline with temps */}
          <div>
            <div className="text-xs text-gray-500 font-medium">CONDITION</div>
            <div className="text-sm font-semibold text-gray-900 capitalize">{forecast.condition.toLowerCase()}</div>
          </div>
        </div>
      </div>
      
      {/* Weather Guidance - Sprint 3 Task 3.3 */}
      {guidance && (
        <div className="mt-3 pt-3 border-t border-blue-200">
          <p className="text-sm text-gray-700 italic">
            {guidance}
          </p>
        </div>
      )}
    </div>
  );
};

export default WorkoutCardWeather;
